import { HourglassEmpty } from '@mui/icons-material';
import { AlertColor, Button } from '@mui/material';
import Form, { FormDataModel } from 'components/Form';
import PageLoader from 'components/PageLoader';
import { RefObject, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import NewBookingSendEmailComponent from 'screens/Authenticated/NewBooking/NewBookingSendEmailComponent';
import {
  appendEmailAttachmentOptions,
  getEmailAttachmentVisibility,
} from 'screens/Authenticated/NewBooking/NewBookingSendEmailComponent/emailAttachmentOptions';
import { NewBookingSendEmailFormComponent } from 'screens/Authenticated/NewBooking/NewBookingSendEmailComponent/SendEmailFormComponent';
import { User } from 'utils/redux/reducer/authentication-slice';
import { removeErrorFieldsFromValues } from 'utils/validators';
import { fetchErrorFields } from 'utils/validators/HelperFunctions';
import strings from '../../../../common/Translation/Translate';
import Notification from '../../../../components/Notification';
import Modal from '../../../../components/modal/Modal.js';
import axiosInstance from '../../../../utils/axios';
import { URLS } from '../../../../utils/constants/urls';
import { bookingDetailToolbarButtonSx } from '../bookingDetailToolbarSx';

export type BookingStatusTransitionAction = 'toTentative' | 'toAwaitingConfirmation';

type ChangeToTentativeButtonButtonProps = {
  id: any;
  action: BookingStatusTransitionAction;
  onSuccess: () => void;
  bookingEmail: string;
  compact?: boolean;
  isContractTemplatePresent?: boolean;
  isProposalTemplatePresent?: boolean;
  isPiTemplatePresent?: boolean;
  isEnquiryTemplatePresent?: boolean;
  isEnquiryStatus?: boolean;
};

const ACTION_CONFIG: Record<
  BookingStatusTransitionAction,
  {
    targetStatus: string;
    label: string;
    useAwaitingConfirmationStyle: boolean;
    tone: 'warning' | 'awaiting';
    modalTitle: string;
    modalBody: string;
    saveButtonText: string;
  }
> = {
  toTentative: {
    targetStatus: 'TENTATIVE',
    label: strings.bookingDetailsChangeToTentativeBooking,
    useAwaitingConfirmationStyle: false,
    tone: 'warning',
    modalTitle: strings.bookingDetailsChangeToTentativeBooking,
    modalBody: strings.bookingDetailsModalBodyMessageTentativeBooking,
    saveButtonText: strings.bookingDetailsChangeToTentativeBooking,
  },
  toAwaitingConfirmation: {
    targetStatus: 'AWAITING_CONFIRMATION',
    label: strings.bookingDetailsChangeToAwaitingConfirmation,
    useAwaitingConfirmationStyle: true,
    tone: 'awaiting',
    modalTitle: strings.bookingDetailsChangeToAwaitingConfirmation,
    modalBody: strings.bookingDetailsModalBodyMessageAwaitingConfirmation,
    saveButtonText: strings.bookingDetailsModalAwaitingConfirmationButton,
  },
};

export const ChangeToTentativeButtonButton: React.FC<ChangeToTentativeButtonButtonProps> = ({
  id,
  action,
  onSuccess,
  bookingEmail,
  compact = false,
  isContractTemplatePresent = false,
  isProposalTemplatePresent = false,
  isPiTemplatePresent = false,
  isEnquiryTemplatePresent = false,
  isEnquiryStatus = false,
}) => {
  const emailAttachmentVisibility = getEmailAttachmentVisibility({
    isContractTemplatePresent,
    isProposalTemplatePresent,
    isPiTemplatePresent,
    isEnquiryTemplatePresent,
    isEnquiryStatus,
  });
  const {
    targetStatus,
    label: buttonLabel,
    useAwaitingConfirmationStyle,
    tone,
    modalTitle,
    modalBody,
    saveButtonText,
  } = ACTION_CONFIG[action];
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  /** Sync guard so a second Save click in the same tick cannot start another PUT/POST. */
  const savingRef = useRef(false);
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({
    display: false,
    severity: null,
    message: '',
  });

  const loggedInUserDetails = useSelector(User);
  let sendEmailFormRef: RefObject<Form | null | undefined> = useRef();
  const [openSendEmailDrawer, setOpenSendEmailDrawer] = useState<boolean>(false);
  const [sendEmailHasError, setSendEmailHasError] = useState<boolean>(false);
  const [sendEmailPageResponse, setSendEmailPageResponse] = useState<any>({});
  const [attachment, setAttachment] = useState<any>([]);
  const [deletedImage] = useState<any>([]);

  const handleClose = () => {
    setOpenModal(false);
  };

  const getFileDetails = (value: string, field: string) => {
    setAttachment(value);
  };

  const handleSaveWithSendEmail = async (e: any) => {
    e.preventDefault();
    if (savingRef.current || isLoading) return;
    savingRef.current = true;
    try {
      const { getFormData } = sendEmailFormRef.current as {
        getFormData: () => { formData: FormDataModel; isFormValid: boolean };
      };

      const { formData, isFormValid } = getFormData();
      setSendEmailHasError(false);

      if (isFormValid) {
        const emailFormData = removeErrorFieldsFromValues(formData);
        setIsLoading(true);
        handleClose();

        const statusRequest = {
          status: targetStatus,
        };

        const { status } = await axiosInstance.put(URLS.bookingStatus(id), statusRequest);
        if (status === 200) {
          const emailRequest = new FormData();
          emailRequest.append('deletedFiles', deletedImage);
          emailRequest.append('to', emailFormData.to);
          emailRequest.append('cc', emailFormData.cc);
          emailRequest.append('bcc', emailFormData.bcc);
          emailRequest.append('type', 'TENTATIVE_BOOKING');

          // Append files if there are any attachments
          attachment.forEach((file: File) => {
            emailRequest.append('files', file);
          });

          appendEmailAttachmentOptions(emailRequest, emailAttachmentVisibility);

          const requestHeaders = {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          };

          const { status } = await axiosInstance.post(
            URLS.sendBookingEmailAttachment(id),
            emailRequest,
            requestHeaders,
          );

          if (status === 200) {
            setIsLoading(false);
            setMessage({
              display: true,
              severity: 'success',
              message: strings.bookingStatusChangeSuccess,
            });
            setTimeout(() => onSuccess(), 1000);
            return true;
          }
        }
      } else {
        setMessage({
          display: true,
          severity: 'error',
          message: `${strings.required_error_prefix_message} ${fetchErrorFields(
            formData,
            NewBookingSendEmailFormComponent(strings),
          ).join(', ')}`,
        });
        setSendEmailHasError(true);
        setIsLoading(false);
      }
    } catch (error: any) {
      setIsLoading(false);
      handleClose();
      if (error.response) {
        setMessage({
          display: true,
          severity: 'error',
          message: error.response.data?.error_description
            ? error.response.data.error_description
            : error.response.data.message,
        });
      } else {
        setMessage({
          display: true,
          severity: 'error',
          message: error.message,
        });
      }
    } finally {
      savingRef.current = false;
    }
  };

  const handleSave = async () => {
    const arr = bookingEmail === '' ? [loggedInUserDetails.email] : [loggedInUserDetails.email, bookingEmail];
    setSendEmailPageResponse({ to: arr.join(',') });
    handleClose();
    setOpenSendEmailDrawer(true);
  };

  const closeNotification = (value: boolean) => {
    setMessage({ ...message, display: value });
  };

  return (
    <>
      {isLoading && <PageLoader />}
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
          autoHideDuration={2000}
        />
      )}
      <Button
        fullWidth={!compact}
        size={compact ? 'small' : 'medium'}
        data-testid={'mainBtn'}
        onClick={() => setOpenModal(true)}
        variant="contained"
        color={useAwaitingConfirmationStyle ? undefined : 'warning'}
        sx={{
          ...(compact ? { ...bookingDetailToolbarButtonSx, my: 0 } : { minHeight: '36px', my: 1 }),
          ...(useAwaitingConfirmationStyle && {
            backgroundColor: '#A3A329 !important',
            color: '#fff !important',
            '&:hover': {
              backgroundColor: '#8F8F24 !important',
              color: '#fff !important',
            },
            '& .MuiButton-startIcon': { color: '#fff !important' },
          }),
        }}
        startIcon={<HourglassEmpty />}>
        {buttonLabel}
      </Button>
      <Modal
        open={openModal}
        setModalClose={handleClose}
        title={undefined}
        size={undefined}
        className={undefined}
        closeButton={false}
        tone={tone}
        icon={<HourglassEmpty />}
        contentTitle={modalTitle}
        contentText={modalBody}
        backButtonText={strings.common_button_back}
        backButtonFunc={handleClose}
        saveButtonText={saveButtonText}
        saveButtonFunc={handleSave}
        loading={isLoading}
      />
      <NewBookingSendEmailComponent
        submitButtonText={strings.common_button_save}
        openDrawer={openSendEmailDrawer}
        formRef={sendEmailFormRef}
        handleCloseDrawer={() => setOpenSendEmailDrawer(false)}
        hasError={sendEmailHasError}
        pageResponse={sendEmailPageResponse}
        handleSave={(e: any) => handleSaveWithSendEmail(e)}
        attachment={attachment}
        deletedImage={deletedImage}
        getFileDetails={getFileDetails}
        disabled={isLoading}
        {...emailAttachmentVisibility}
      />
    </>
  );
};
export default ChangeToTentativeButtonButton;
